import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Group,
  Stack,
  Title,
  Text,
  Badge,
  Card,
  Grid,
  Button,
  Box,
  Modal,
  Divider,
  Avatar,
  Tabs,
  Center,
  Loader,
  SimpleGrid,
  ScrollArea,
  Skeleton,
  Paper
} from '@mantine/core';
import {
  IconCheck,
  IconClipboardX,
  IconX,
  IconFolder,
  IconFileText,
  IconExternalLink,
  IconUsers,
  IconDownload
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import { formatLocalDate } from 'utils/convertLocalFormat';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import LIAReportModal from 'components/LIA/LIAReportModal';
import DownloadModal from 'components/LIA/DownloadModal';
import SubjectMatterExperts from 'modules/pages/lia/AssessmentSchedules/SubjectMatterExperts';
import DetailInformationSection from 'modules/pages/lia/AssessmentSchedules/DetailInformationSection';
import ModalRespondentResponse from 'modules/pages/lia/AssessmentSchedules/ModalRespondentResponse';
import ModuleSection from 'modules/pages/lia/AssessmentSchedules/ModuleSection';
import TableAssessmentParticipants from 'modules/pages/lia/AssessmentSchedules/TableAssessmentParticipants';
import TableAssessmentRespondents from 'modules/pages/lia/AssessmentSchedules/TableAssessmentRespondents';
import ModalNoteAssessment from 'modules/pages/lia/AssessmentSchedules/ModalNoteAssessment';

const AssessmentDetailPage: React.FC = () => {
  /* eslint-disable */
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'modules' | 'participants'>('modules');
  const [activeModuleIndex, setActiveModuleIndex] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [notesModalOpened, setNotesModalOpened] = useState(false);
  const itemsPerPage = 5;

  const [responseModalOpened, setResponseModalOpened] = useState(false);
  const [selectedRespondent, setSelectedRespondent] = useState<any>(null);
  const [evidenceModalOpened, setEvidenceModalOpened] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<any>(null);
  const [participantSelfAssessment, setParticipantSelfAssessment] = useState<any[]>([]);
  const [downloadModalOpened, setDownloadModalOpened] = useState(false);

  const [liaReportModalOpened, setLiaReportModalOpened] = useState(false);
  const [liaReportContent, setLiaReportContent] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedCategoryForLIA, setSelectedCategoryForLIA] = useState<string | undefined>();

  const toggleDownloadModal = () => setDownloadModalOpened((prev) => !prev);
  const toggleLiaReportModal = (selectedCategory?: string) => {
    setSelectedCategoryForLIA(selectedCategory);
    setLiaReportModalOpened((prev) => !prev);
  };

  const {
    data: assessment,
    isLoading,
    error
  } = useQuery({
    queryKey: ['assessmentSchedule', id],
    queryFn: () => assessmentScheduleService.getDetail(id!),
    enabled: !!id,
    refetchOnWindowFocus: false
  });

  const toggleNotesModal = () => setNotesModalOpened((prev) => !prev);
  /* eslint-enable */

  // Initialize active module when data is loaded
  useEffect(() => {
    if (assessment?.modules && assessment.modules.length > 0) {
      setActiveModuleIndex(0);
    }
  }, [assessment]);

  // Find self-assessment responses for a specific participant
  useEffect(() => {
    if (selectedParticipant && assessment?.respondents) {
      // Look for self-assessment responses where the participant is the binusianId
      const selfAssessments = assessment.respondents
        .filter(
          (resp: { respondentType: string; binusianID: any }) =>
            resp.respondentType === 'Self' && resp.binusianID === selectedParticipant.binusianID
        )
        .flatMap((resp: { responses: any }) => resp.responses || []);

      setParticipantSelfAssessment(selfAssessments);
    } else {
      setParticipantSelfAssessment([]);
    }
  }, [selectedParticipant, assessment]);

  // Get questions for the active module
  const getActiveModuleQuestions = () => {
    if (
      !assessment?.modules ||
      assessment.modules.length === 0 ||
      activeModuleIndex >= assessment.modules.length
    ) {
      return [];
    }

    const activeModule = assessment.modules[activeModuleIndex];
    return activeModule.questions || [];
  };

  const activeModuleQuestions = getActiveModuleQuestions();
  /* eslint-disable */
  const totalPages = Math.ceil(activeModuleQuestions.length / itemsPerPage);
  const paginatedQuestions = activeModuleQuestions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  /* eslint-enable */

  useEffect(() => {
    setCurrentPage(1);
  }, [activeModuleIndex]);

  // Helper function to exclude unwanted columns
  const excludeColumns = (data: any[]) => {
    const columnsToExclude = [
      'answerId',
      'msAssessmentScheduleID',
      'participantID',
      'respondentID',
      'questionID'
    ];

    return data.map((item) => {
      const cleanedItem = { ...item };
      columnsToExclude.forEach((col) => delete cleanedItem[col]);
      return cleanedItem;
    });
  };

  // Helper function to reorder columns
  const reorderColumns = (data: any[], type: 'external' | 'participant' | 'respondent') => {
    return data.map((item) => {
      const reorderedItem: any = {};

      // Add all fields except the ones we want to reposition
      Object.keys(item).forEach((key) => {
        if (!['category', 'skalaCategory', 'questionType', 'participantName'].includes(key)) {
          reorderedItem[key] = item[key];
        }
      });

      // Insert questionType first, then skalaCategory, then category
      if (item.questionType !== undefined) reorderedItem.questionType = item.questionType;
      if (item.skalaCategory !== undefined) reorderedItem.skalaCategory = item.skalaCategory;
      if (item.category !== undefined) reorderedItem.category = item.category;

      // For respondent tab, move participantName after participantBinusianID
      if (type === 'respondent' && item.participantName !== undefined) {
        const keys = Object.keys(reorderedItem);
        const binusianIdIndex = keys.findIndex((key) => key === 'participantBinusianID');

        if (binusianIdIndex !== -1) {
          const newItem: any = {};
          keys.forEach((key, index) => {
            newItem[key] = reorderedItem[key];
            if (index === binusianIdIndex) {
              newItem.participantName = item.participantName;
            }
          });
          return newItem;
        } else {
          reorderedItem.participantName = item.participantName;
        }
      }

      return reorderedItem;
    });
  };

  // Helper: map External Answers to required columns/order
  const mapExternalAnswers = (externalAnswers: any[] = [], modulesData: any[] = []) => {
    if (!Array.isArray(externalAnswers)) return [] as any[];

    const moduleMap = new Map<string, string>();
    (modulesData || []).forEach((m: any) => {
      if (m && m.moduleID) moduleMap.set(m.moduleID, m.moduleName || m.moduleID);
    });

    const toStr = (v: any) => (v === undefined || v === null ? '' : v);

    return externalAnswers.map((item: any) => {
      const moduleID = item?.moduleID || '';
      const moduleName = moduleMap.get(moduleID) || item?.moduleName || moduleID;
      const participantBinusianID = item?.participantBinusianID || item?.binusianID || '';
      const participantName = item?.participantName || item?.name || '';
      const participantEmail = item?.participantEmail || item?.emailParticipant || '';
      const externalEmail = item?.externalEmail || item?.email || '';
      const responseType = item?.respondentType || item?.responseType || 'External';
      const questionType = item?.questionType || item?.type || '';
      const answeredDate = item?.answeredDate || item?.dateIn || '';

      // Preserve exact column order as requested
      return {
        trAssessmentRecordID: toStr(item?.trAssessmentRecordID || item?.answerId || ''),
        moduleID: toStr(moduleID),
        moduleName: toStr(moduleName),
        responseType: toStr(responseType),
        externalEmail: toStr(externalEmail),
        participantBinusianID: toStr(participantBinusianID),
        participantName: toStr(participantName),
        participantEmail: toStr(participantEmail),
        questionTitle: toStr(item?.questionTitle || item?.title || ''),
        answer: toStr(item?.answer || ''),
        answerLabel: toStr(item?.answerLabel || ''),
        questionType: toStr(questionType),
        skalaCategory: toStr(item?.skalaCategory || ''),
        category: toStr(item?.category || ''),
        answeredDate: toStr(answeredDate)
      } as const;
    });
  };

  // Helper function to create half-baked sheets (questions as columns, users as rows)
  const createHalfBakedSheet = (
    answers: any[],
    sheetType: 'internal' | 'external' | 'participant'
  ) => {
    if (!answers || answers.length === 0) return [];

    // Get unique questions and sort by type (Range first, then Open)
    const questionMap = new Map<string, any>();
    answers.forEach((item: any) => {
      const questionKey = item.questionTitle || item.title;
      if (questionKey && !questionMap.has(questionKey)) {
        questionMap.set(questionKey, {
          title: questionKey,
          type: item.questionType || item.type || 'Open',
          skalaCategory: item.skalaCategory || '',
          category: item.category || ''
        });
      }
    });

    // Sort questions: Range type first, then Open questions
    const sortedQuestions = Array.from(questionMap.values()).sort((a, b) => {
      if (a.type === 'Range' && b.type !== 'Range') return -1;
      if (a.type !== 'Range' && b.type === 'Range') return 1;
      return a.title.localeCompare(b.title);
    });

    // Get unique users based on sheet type
    const userMap = new Map<string, any>();
    answers.forEach((item: any) => {
      let userKey = '';
      let userName = '';
      let userEmail = '';
      let userBinusianID = '';

      if (sheetType === 'external') {
        userKey = item.externalEmail || item.email || 'Unknown';
        userEmail = item.externalEmail || item.email || '';
        userName = item.externalEmail || item.email || '';
      } else if (sheetType === 'participant') {
        userKey = item.binusianID || item.participantBinusianID || item.email || 'Unknown';
        userName = item.name || item.participantName || '';
        userEmail = item.email || item.participantEmail || '';
        userBinusianID = item.binusianID || item.participantBinusianID || '';
      } else {
        // internal respondent
        userKey = item.binusianID || item.email || item.name || 'Unknown';
        userName = item.name || item.participantName || '';
        userEmail = item.email || item.participantEmail || '';
        userBinusianID = item.binusianID || item.participantBinusianID || '';
      }

      if (!userMap.has(userKey)) {
        userMap.set(userKey, {
          name: userName,
          email: userEmail,
          binusianID: userBinusianID,
          answers: new Map()
        });
      }

      // Store answer for this user and question
      const questionKey = item.questionTitle || item.title;
      const user = userMap.get(userKey);
      if (questionKey && user) {
        // For Range questions, use the numeric value; for others use answerLabel or answer
        const questionType = item.questionType || item.type || 'Open';
        const answerValue =
          questionType === 'Range' ? item.answer || '' : item.answerLabel || item.answer || '';
        user.answers.set(questionKey, answerValue);
      }
    });

    // Build the sheet data
    const sheetData: any[] = [];
    userMap.forEach((user) => {
      const row: any = {};

      // Add user identification columns
      if (sheetType === 'external') {
        row['Email'] = user.email;
      } else if (sheetType === 'participant') {
        row['ID'] = user.binusianID;
        row['Nama'] = user.name;
      } else {
        // internal respondent
        row['ID'] = user.binusianID;
        row['Nama'] = user.name;
      }

      // Add question columns in sorted order
      sortedQuestions.forEach((question) => {
        const answer = user.answers.get(question.title) || '';
        row[question.title] = answer;
      });

      sheetData.push(row);
    });

    return sheetData;
  };

  // Helper function to create general analysis data
  const createGeneralAnalysisData = (data: any, beforeData?: any) => {
    const analysisData: any[] = [];

    if (!data || !data.participantAnswers || !data.respondentAnswers) {
      return analysisData;
    }

    // Helper function to process assessment data
    const processAssessmentData = (
      participantAnswers: any[],
      respondentAnswers: any[],
      prefix: string = ''
    ) => {
      // Group participant answers (self-assessment)
      const selfAssessmentMap = new Map();
      participantAnswers
        .filter((item: any) => item.respondentType === 'Self')
        .forEach((item: any) => {
          const questionLabel = item.questionTitle || item.title || item.moduleName;
          const key = `${item.name}_${questionLabel}_${item.category}_${item.skalaCategory}`;
          if (!selfAssessmentMap.has(key)) {
            selfAssessmentMap.set(key, {
              participantName: item.name,
              participantEmail: item.email,
              questionLabel: questionLabel,
              category: item.category,
              skalaCategory: item.skalaCategory,
              scores: [],
              count: 0
            });
          }

          const entry = selfAssessmentMap.get(key);
          // Only include Range-type questions for analysis
          const questionType = item.questionType || item.type || 'Open';
          if (questionType === 'Range' && item.answer && !isNaN(parseFloat(item.answer))) {
            entry.scores.push(parseFloat(item.answer));
            entry.count++;
          }
        });

      // Group respondent answers by participant and type
      const respondentAssessmentMap = new Map();
      respondentAnswers.forEach((item: any) => {
        const questionLabel = item.questionTitle || item.title || item.moduleName;
        // Use participant info from the respondent data
        const participantName = item.participantName || item.name || 'Unknown Participant';
        const participantEmail = item.participantEmail || item.email || '';
        const key = `${participantName}_${questionLabel}_${item.category}_${item.skalaCategory}_${item.respondentType}`;
        if (!respondentAssessmentMap.has(key)) {
          respondentAssessmentMap.set(key, {
            participantName: participantName,
            participantEmail: participantEmail,
            questionLabel: questionLabel,
            category: item.category,
            skalaCategory: item.skalaCategory,
            respondentType: item.respondentType,
            scores: [],
            count: 0
          });
        }

        const entry = respondentAssessmentMap.get(key);
        // Only include Range-type questions for analysis
        const questionType = item.questionType || item.type || 'Open';
        if (questionType === 'Range' && item.answer && !isNaN(parseFloat(item.answer))) {
          entry.scores.push(parseFloat(item.answer));
          entry.count++;
        }
      });

      // Calculate averages and create analysis records
      const participantGroups = new Map();

      // Process self-assessment averages
      selfAssessmentMap.forEach((selfData) => {
        const groupKey = `${selfData.participantName}_${selfData.questionLabel}_${selfData.category}_${selfData.skalaCategory}`;

        if (!participantGroups.has(groupKey)) {
          participantGroups.set(groupKey, {
            questionLabel: selfData.questionLabel,
            category: selfData.category,
            skalaCategory: selfData.skalaCategory,
            participantName: selfData.participantName,
            participantEmail: selfData.participantEmail,
            selfAssessmentAvg: 0,
            peerScores: [],
            subordinateScores: [],
            supervisorScores: []
          });
        }

        const group = participantGroups.get(groupKey);
        group.selfAssessmentAvg =
          selfData.scores.length > 0
            ? parseFloat(
                (
                  selfData.scores.reduce((a: number, b: number) => a + b, 0) /
                  selfData.scores.length
                ).toFixed(2)
              )
            : 0;
      });

      // Process respondent assessments
      respondentAssessmentMap.forEach((respData) => {
        const groupKey = `${respData.participantName}_${respData.questionLabel}_${respData.category}_${respData.skalaCategory}`;

        if (!participantGroups.has(groupKey)) {
          participantGroups.set(groupKey, {
            questionLabel: respData.questionLabel,
            category: respData.category,
            skalaCategory: respData.skalaCategory,
            participantName: respData.participantName,
            participantEmail: respData.participantEmail,
            selfAssessmentAvg: 0,
            peerScores: [],
            subordinateScores: [],
            supervisorScores: []
          });
        }

        const group = participantGroups.get(groupKey);
        const avgScore =
          respData.scores.length > 0
            ? parseFloat(
                (
                  respData.scores.reduce((a: number, b: number) => a + b, 0) /
                  respData.scores.length
                ).toFixed(2)
              )
            : 0;

        if (respData.respondentType === 'Peer') {
          group.peerScores.push(avgScore);
        } else if (respData.respondentType === 'Subordinate') {
          group.subordinateScores.push(avgScore);
        } else if (respData.respondentType === 'Supervisor') {
          group.supervisorScores.push(avgScore);
        }
      });

      // Calculate final averages
      const results = new Map();
      participantGroups.forEach((group) => {
        const peerAvg =
          group.peerScores.length > 0
            ? parseFloat(
                (
                  group.peerScores.reduce((a: number, b: number) => a + b, 0) /
                  group.peerScores.length
                ).toFixed(2)
              )
            : 0;

        const subAvg =
          group.subordinateScores.length > 0
            ? parseFloat(
                (
                  group.subordinateScores.reduce((a: number, b: number) => a + b, 0) /
                  group.subordinateScores.length
                ).toFixed(2)
              )
            : 0;

        const spvAvg =
          group.supervisorScores.length > 0
            ? parseFloat(
                (
                  group.supervisorScores.reduce((a: number, b: number) => a + b, 0) /
                  group.supervisorScores.length
                ).toFixed(2)
              )
            : 0;

        // Calculate 360/180 average (average of peer, subordinate, supervisor)
        const respondentScores = [peerAvg, subAvg, spvAvg].filter((score) => score > 0);
        const avg360180 =
          respondentScores.length > 0
            ? parseFloat(
                (respondentScores.reduce((a, b) => a + b, 0) / respondentScores.length).toFixed(2)
              )
            : 0;

        const resultKey = `${group.participantName}_${group.questionLabel}_${group.category}_${group.skalaCategory}`;
        results.set(resultKey, {
          [`${prefix}selfAssessmentAvgScore`]: group.selfAssessmentAvg,
          [`${prefix}avg360180Score`]: avg360180,
          [`${prefix}peerAvgAssessmentScore`]: peerAvg,
          [`${prefix}subordinateAvgAssessmentScore`]: subAvg,
          [`${prefix}supervisorAvgAssessmentScore`]: spvAvg
        });
      });

      return results;
    };

    // Process current assessment data
    const currentResults = processAssessmentData(data.participantAnswers, data.respondentAnswers);

    // Process previous assessment data if available
    let previousResults = new Map();
    const hasPreviousData =
      beforeData && beforeData.participantAnswers && beforeData.respondentAnswers;

    if (hasPreviousData) {
      previousResults = processAssessmentData(
        beforeData.participantAnswers,
        beforeData.respondentAnswers,
        'previous'
      );
    }

    // Combine results - get all unique participant-question-category-skala combinations
    const allKeys = new Set([...currentResults.keys(), ...previousResults.keys()]);

    // Separate self-assessment keys and respondent keys
    const selfAssessmentKeys: string[] = [];
    const respondentKeys: string[] = [];

    // First pass: identify which keys are from self-assessment
    data.participantAnswers
      .filter((item: any) => item.respondentType === 'Self')
      .forEach((item: any) => {
        const questionLabel = item.questionTitle || item.title || item.moduleName;
        const key = `${item.name}_${questionLabel}_${item.category}_${item.skalaCategory}`;
        if (allKeys.has(key) && !selfAssessmentKeys.includes(key)) {
          selfAssessmentKeys.push(key);
        }
      });

    // Second pass: identify which keys are from respondents only
    allKeys.forEach((key) => {
      if (!selfAssessmentKeys.includes(key)) {
        respondentKeys.push(key);
      }
    });

    // Process self-assessment data first
    selfAssessmentKeys.forEach((key) => {
      const [participantName, questionLabel, category, skalaCategory] = key.split('_');

      const currentData = currentResults.get(key) || {
        selfAssessmentAvgScore: 0,
        avg360180Score: 0,
        peerAvgAssessmentScore: 0,
        subordinateAvgAssessmentScore: 0,
        supervisorAvgAssessmentScore: 0
      };

      const previousData = previousResults.get(key) || {
        previousselfAssessmentAvgScore: 0,
        previousavg360180Score: 0,
        previouspeerAvgAssessmentScore: 0,
        previoussubordinateAvgAssessmentScore: 0,
        previoussupervisorAvgAssessmentScore: 0
      };

      // Find participant email from participant answers (self-assessment)
      let participantEmail = '';
      if (data.participantAnswers) {
        const participantData = data.participantAnswers.find(
          (item: any) =>
            item.name === participantName &&
            (item.questionTitle || item.title) === questionLabel &&
            item.category === category &&
            item.skalaCategory === skalaCategory
        );
        participantEmail = participantData?.email || '';
      }

      if (!participantEmail && hasPreviousData) {
        const participantData = beforeData.participantAnswers.find(
          (item: any) =>
            item.name === participantName &&
            (item.questionTitle || item.title) === questionLabel &&
            item.category === category &&
            item.skalaCategory === skalaCategory
        );
        participantEmail = participantData?.email || '';
      }

      analysisData.push({
        dataType: 'Participant', // Flag to identify data type
        questionLabel,
        category,
        skalaCategory,
        participantName,
        participantEmail,
        // Current assessment scores
        selfAssessmentAvgScore: currentData.selfAssessmentAvgScore,
        avg360180Score: currentData.avg360180Score,
        peerAvgAssessmentScore: currentData.peerAvgAssessmentScore,
        subordinateAvgAssessmentScore: currentData.subordinateAvgAssessmentScore,
        supervisorAvgAssessmentScore: currentData.supervisorAvgAssessmentScore,
        // Previous assessment scores (only if data exists)
        ...(hasPreviousData && {
          previousSelfAssessmentAvgScore: previousData.previousselfAssessmentAvgScore,
          previousAvg360180Score: previousData.previousavg360180Score,
          previousPeerAvgAssessmentScore: previousData.previouspeerAvgAssessmentScore,
          previousSubordinateAvgAssessmentScore: previousData.previoussubordinateAvgAssessmentScore,
          previousSupervisorAvgAssessmentScore: previousData.previoussupervisorAvgAssessmentScore
        })
      });
    });

    // Process respondent-only data
    respondentKeys.forEach((key) => {
      const [participantName, questionLabel, category, skalaCategory] = key.split('_');

      const currentData = currentResults.get(key) || {
        selfAssessmentAvgScore: 0,
        avg360180Score: 0,
        peerAvgAssessmentScore: 0,
        subordinateAvgAssessmentScore: 0,
        supervisorAvgAssessmentScore: 0
      };

      const previousData = previousResults.get(key) || {
        previousselfAssessmentAvgScore: 0,
        previousavg360180Score: 0,
        previouspeerAvgAssessmentScore: 0,
        previoussubordinateAvgAssessmentScore: 0,
        previoussupervisorAvgAssessmentScore: 0
      };

      // Find participant email from respondent answers
      let participantEmail = '';
      if (data.respondentAnswers) {
        const respondentData = data.respondentAnswers.find(
          (item: any) =>
            (item.participantName || item.name) === participantName &&
            (item.questionTitle || item.title) === questionLabel &&
            item.category === category &&
            item.skalaCategory === skalaCategory
        );
        participantEmail = respondentData?.participantEmail || respondentData?.email || '';
      }

      if (!participantEmail && hasPreviousData) {
        const respondentData = beforeData.respondentAnswers.find(
          (item: any) =>
            (item.participantName || item.name) === participantName &&
            (item.questionTitle || item.title) === questionLabel &&
            item.category === category &&
            item.skalaCategory === skalaCategory
        );
        participantEmail = respondentData?.participantEmail || respondentData?.email || '';
      }

      analysisData.push({
        dataType: 'Respondent', // Flag to identify data type
        questionLabel,
        category,
        skalaCategory,
        participantName,
        participantEmail,
        // Current assessment scores
        selfAssessmentAvgScore: currentData.selfAssessmentAvgScore,
        avg360180Score: currentData.avg360180Score,
        peerAvgAssessmentScore: currentData.peerAvgAssessmentScore,
        subordinateAvgAssessmentScore: currentData.subordinateAvgAssessmentScore,
        supervisorAvgAssessmentScore: currentData.supervisorAvgAssessmentScore,
        // Previous assessment scores (only if data exists)
        ...(hasPreviousData && {
          previousSelfAssessmentAvgScore: previousData.previousselfAssessmentAvgScore,
          previousAvg360180Score: previousData.previousavg360180Score,
          previousPeerAvgAssessmentScore: previousData.previouspeerAvgAssessmentScore,
          previousSubordinateAvgAssessmentScore: previousData.previoussubordinateAvgAssessmentScore,
          previousSupervisorAvgAssessmentScore: previousData.previoussupervisorAvgAssessmentScore
        })
      });
    });

    // Sort: Participants first, then Respondents, then by question, category, etc.
    return analysisData.sort((a, b) => {
      // First sort by data type (Participant before Respondent)
      if (a.dataType !== b.dataType) {
        return a.dataType === 'Participant' ? -1 : 1;
      }
      // Then by question label
      if (a.questionLabel !== b.questionLabel)
        return a.questionLabel.localeCompare(b.questionLabel);
      // Then by category
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      // Then by skala category
      if (a.skalaCategory !== b.skalaCategory)
        return a.skalaCategory.localeCompare(b.skalaCategory);
      // Finally by participant name
      return a.participantName.localeCompare(b.participantName);
    });
  };

  // Export unanswered participants
  const handleExportUnansweredParticipants = () => {
    if (!assessment || !assessment.participants) return;

    // Filter participants who haven't answered
    const unansweredParticipants = assessment.participants.filter(
      (participant: any) => !participant.isAnswer
    );

    if (unansweredParticipants.length === 0) {
      alert('All participants have completed their assessments!');
      return;
    }

    // Prepare data for export with link
    const exportData = unansweredParticipants.map((participant: any) => ({
      Name: participant.name || participant.binusianID || '-',
      Email: participant.email || '-',
      'Assessment Link':
        participant.assessmentUrl ||
        participant.link ||
        `${window.location.origin}/app/lia/assessment/${id}/participant/${participant.binusianID}` ||
        '-'
    }));

    // Create workbook and export
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Unanswered Participants');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const filename = `${assessment?.name?.replace(/\s+/g, '_')}_Unanswered_Participants_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, filename);
  };

  // Export unanswered respondents
  const handleExportUnansweredRespondents = () => {
    if (!assessment || !assessment.respondents) return;

    // Filter respondents who haven't answered
    const unansweredRespondents = assessment.respondents.filter(
      (respondent: any) => !respondent.responses || respondent.responses.length === 0
    );

    if (unansweredRespondents.length === 0) {
      alert('All respondents have completed their assessments!');
      return;
    }

    // Prepare data for export with link
    const exportData = unansweredRespondents.map((respondent: any) => ({
      Name: respondent.name || respondent.binusianID || '-',
      Email: respondent.email || '-',
      'Respondent Type': respondent.respondentType || '-',
      'Assessment Link':
        respondent.assessmentUrl ||
        respondent.link ||
        `${window.location.origin}/app/lia/assessment/${id}/respondent/${respondent.binusianID}` ||
        '-'
    }));

    // Create workbook and export
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Unanswered Respondents');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const filename = `${assessment?.name?.replace(/\s+/g, '_')}_Unanswered_Respondents_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, filename);
  };

  // Main handleDownloadRawData function
  const handleDownloadRawData = async (selectedCategory?: string) => {
    if (!id) return;

    try {
      setIsDownloading(true);

      try {
        const responseBlob = await assessmentScheduleService.exportAssessmentData(
          id,
          selectedCategory
        );

        const jsonText = await responseBlob.text();
        const jsonData = JSON.parse(jsonText);

        if (jsonData.data) {
          const workbook = XLSX.utils.book_new();

          const afterData = jsonData.data.answerData || jsonData;
          const beforeData = jsonData.data.dataBefore?.answerData;
          const hasBeforeData = beforeData && Object.keys(beforeData).length > 0;

          const createComparisonData = (
            beforeAnswers: any[] = [],
            afterAnswers: any[] = [],
            comparisonType: 'external' | 'participant' | 'respondent',
            modulesData: any[] = []
          ) => {
            const comparisonData: any[] = [];

            // Create a map for moduleID to moduleName lookup
            const moduleMap = new Map();
            modulesData.forEach((module) => {
              moduleMap.set(module.moduleID, module.moduleName || module.moduleID);
            });

            const getModuleName = (moduleID: string) => {
              return moduleMap.get(moduleID) || moduleID || 'Unknown Module';
            };

            const beforeMap = new Map();
            const afterMap = new Map();

            const getKeyField = (item: any) => {
              switch (comparisonType) {
                case 'external':
                  return item.email || 'Unknown Email';
                case 'participant':
                  return item.name || item.email || 'Unknown Participant';
                case 'respondent':
                  return item.name || item.email || 'Unknown Respondent';
                default:
                  return 'Unknown';
              }
            };

            // Clean and process before data
            const cleanedBeforeAnswers = excludeColumns(beforeAnswers);
            cleanedBeforeAnswers.forEach((item) => {
              const userKey = getKeyField(item);
              const moduleId = item.moduleID || 'Unknown Module';
              const respondentType = item.respondentType || 'N/A';
              const key = `${userKey}_${moduleId}_${item.questionTitle}_${respondentType}`;
              beforeMap.set(key, item);
            });

            // Clean and process after data
            const cleanedAfterAnswers = excludeColumns(afterAnswers);
            cleanedAfterAnswers.forEach((item) => {
              const userKey = getKeyField(item);
              const moduleId = item.moduleID || 'Unknown Module';
              const respondentType = item.respondentType || 'N/A';
              const key = `${userKey}_${moduleId}_${item.questionTitle}_${respondentType}`;
              afterMap.set(key, item);
            });

            // Get all unique keys from both periods
            const allKeys = new Set([...beforeMap.keys(), ...afterMap.keys()]);

            allKeys.forEach((key) => {
              const beforeItem = beforeMap.get(key);
              const afterItem = afterMap.get(key);

              // Get base info from available item
              const sampleItem = beforeItem || afterItem;
              const moduleID = sampleItem?.moduleID || 'Unknown Module';
              const moduleName = getModuleName(moduleID);

              // Base comparison object
              const comparisonItem: any = {
                // User identification based on type
                ...(comparisonType === 'external' && {
                  email: sampleItem?.email || 'Unknown Email'
                }),
                ...(comparisonType === 'participant' && {
                  participantName: sampleItem?.name || 'Unknown',
                  participantEmail: sampleItem?.email || 'Unknown',
                  respondentType: sampleItem?.respondentType || 'Unknown'
                }),
                ...(comparisonType === 'respondent' && {
                  respondentName: sampleItem?.name || 'Unknown',
                  respondentEmail: sampleItem?.email || 'Unknown',
                  respondentType: sampleItem?.respondentType || 'Unknown'
                }),

                // Module and Question details
                moduleID: moduleID,
                moduleName: moduleName,
                questionTitle: sampleItem?.questionTitle,
                questionType: sampleItem?.questionType,
                skalaCategory: sampleItem?.skalaCategory,
                category: sampleItem?.category,

                // Before data
                beforeAnswer: beforeItem?.answer || 'No Data',
                beforeAnswerLabel: beforeItem?.answerLabel || beforeItem?.answer || 'No Data',

                // After data
                afterAnswer: afterItem?.answer || 'No Data',
                afterAnswerLabel: afterItem?.answerLabel || afterItem?.answer || 'No Data'
              };

              // Calculate differences based on question type
              if (sampleItem?.questionType === 'Range') {
                const beforeValue = beforeItem?.answer ? parseFloat(beforeItem.answer) : null;
                const afterValue = afterItem?.answer ? parseFloat(afterItem.answer) : null;

                if (beforeValue !== null && afterValue !== null) {
                  const difference = afterValue - beforeValue;
                  comparisonItem.difference = difference;
                  comparisonItem.changeType =
                    difference > 0 ? 'Improved' : difference < 0 ? 'Declined' : 'No Change';
                } else {
                  comparisonItem.difference = 'N/A';
                  comparisonItem.changeType =
                    beforeValue === null && afterValue === null
                      ? 'No Data Both'
                      : beforeValue === null
                        ? 'New Response'
                        : 'Missing After';
                }
              } else {
                comparisonItem.difference = 'N/A';
                comparisonItem.changeType =
                  beforeItem && afterItem
                    ? 'Both Periods'
                    : beforeItem
                      ? 'Before Only'
                      : 'After Only';
              }

              // Add data status
              comparisonItem.dataStatus =
                beforeItem && afterItem
                  ? 'Both Periods'
                  : beforeItem
                    ? 'Before Only'
                    : 'After Only';

              // Add timestamps
              comparisonItem.beforeTimestamp =
                beforeItem?.answeredDate || beforeItem?.dateIn || 'N/A';
              comparisonItem.afterTimestamp = afterItem?.answeredDate || afterItem?.dateIn || 'N/A';

              comparisonData.push(comparisonItem);
            });

            return comparisonData.sort((a, b) => {
              const userA =
                comparisonType === 'external'
                  ? a.email
                  : comparisonType === 'participant'
                    ? a.participantName
                    : a.respondentName;
              const userB =
                comparisonType === 'external'
                  ? b.email
                  : comparisonType === 'participant'
                    ? b.participantName
                    : b.respondentName;

              if (userA !== userB) {
                return userA.localeCompare(userB);
              }
              if (a.moduleName !== b.moduleName) {
                return a.moduleName.localeCompare(b.moduleName);
              }
              if (a.questionTitle !== b.questionTitle) {
                return a.questionTitle.localeCompare(b.questionTitle);
              }
              return (a.respondentType || '').localeCompare(b.respondentType || '');
            });
          };

          // Process and clean current data; map External to requested columns
          const processedAfterData = {
            externalAnswers: afterData.externalAnswers || [],
            participantAnswers: afterData.participantAnswers
              ? reorderColumns(excludeColumns(afterData.participantAnswers), 'participant')
              : [],
            respondentAnswers: afterData.respondentAnswers
              ? reorderColumns(excludeColumns(afterData.respondentAnswers), 'respondent')
              : []
          };

          // External Respondent sheet with restricted columns (only if data exists)
          const externalRows = mapExternalAnswers(
            processedAfterData.externalAnswers,
            assessment?.modules || []
          );
          if (externalRows.length > 0) {
            const wsExternal = XLSX.utils.json_to_sheet(externalRows);
            XLSX.utils.book_append_sheet(workbook, wsExternal, 'External Respondent');

            // Half Baked External Respondent sheet (only if data exists)
            const halfBakedExternal = createHalfBakedSheet(
              processedAfterData.externalAnswers,
              'external'
            );
            if (halfBakedExternal.length > 0) {
              const wsHalfBakedExternal = XLSX.utils.json_to_sheet(halfBakedExternal);
              XLSX.utils.book_append_sheet(
                workbook,
                wsHalfBakedExternal,
                'Half Baked External Respondent'
              );
            }
          }

          // Participant Answers sheet (only if data exists)
          if (processedAfterData.participantAnswers.length > 0) {
            const wsParticipants = XLSX.utils.json_to_sheet(processedAfterData.participantAnswers);
            XLSX.utils.book_append_sheet(workbook, wsParticipants, 'Participant Answers');

            // Half Baked Participant sheet (only if data exists)
            const halfBakedParticipant = createHalfBakedSheet(
              afterData.participantAnswers || [],
              'participant'
            );
            if (halfBakedParticipant.length > 0) {
              const wsHalfBakedParticipant = XLSX.utils.json_to_sheet(halfBakedParticipant);
              XLSX.utils.book_append_sheet(
                workbook,
                wsHalfBakedParticipant,
                'Half Baked Participant'
              );
            }
          }

          // Respondent Answers sheet (only if data exists)
          if (processedAfterData.respondentAnswers.length > 0) {
            const wsRespondents = XLSX.utils.json_to_sheet(processedAfterData.respondentAnswers);
            XLSX.utils.book_append_sheet(workbook, wsRespondents, 'Respondent Answers');

            // Half Baked Internal Respondent sheet (only if data exists)
            const halfBakedInternal = createHalfBakedSheet(
              afterData.respondentAnswers || [],
              'internal'
            );
            if (halfBakedInternal.length > 0) {
              const wsHalfBakedInternal = XLSX.utils.json_to_sheet(halfBakedInternal);
              XLSX.utils.book_append_sheet(
                workbook,
                wsHalfBakedInternal,
                'Half Baked Internal Respondent'
              );
            }
          }

          // Add General Analysis tab (only if data exists)
          const generalAnalysisData = createGeneralAnalysisData(afterData, beforeData);
          if (generalAnalysisData.length > 0) {
            const wsGeneralAnalysis = XLSX.utils.json_to_sheet(generalAnalysisData);
            XLSX.utils.book_append_sheet(workbook, wsGeneralAnalysis, 'General Analysis');
          }

          if (hasBeforeData) {
            const modulesData = assessment?.modules || [];

            // Process before data for half-baked sheets
            const processedBeforeData = {
              externalAnswers: beforeData.externalAnswers || [],
              participantAnswers: beforeData.participantAnswers
                ? reorderColumns(excludeColumns(beforeData.participantAnswers), 'participant')
                : [],
              respondentAnswers: beforeData.respondentAnswers
                ? reorderColumns(excludeColumns(beforeData.respondentAnswers), 'respondent')
                : []
            };

            // Half Baked Before External Respondent sheet
            if (beforeData.externalAnswers && beforeData.externalAnswers.length > 0) {
              const halfBakedBeforeExternal = createHalfBakedSheet(
                processedBeforeData.externalAnswers,
                'external'
              );
              if (halfBakedBeforeExternal.length > 0) {
                const wsHalfBakedBeforeExternal = XLSX.utils.json_to_sheet(halfBakedBeforeExternal);
                XLSX.utils.book_append_sheet(
                  workbook,
                  wsHalfBakedBeforeExternal,
                  'Before External Respondent'
                );
              }
            }

            // Half Baked Before Participant sheet
            if (beforeData.participantAnswers && beforeData.participantAnswers.length > 0) {
              const halfBakedBeforeParticipant = createHalfBakedSheet(
                beforeData.participantAnswers || [],
                'participant'
              );
              if (halfBakedBeforeParticipant.length > 0) {
                const wsHalfBakedBeforeParticipant = XLSX.utils.json_to_sheet(
                  halfBakedBeforeParticipant
                );
                XLSX.utils.book_append_sheet(
                  workbook,
                  wsHalfBakedBeforeParticipant,
                  'Before Participant'
                );
              }
            }

            // Half Baked Before Internal Respondent sheet
            if (beforeData.respondentAnswers && beforeData.respondentAnswers.length > 0) {
              const halfBakedBeforeInternal = createHalfBakedSheet(
                beforeData.respondentAnswers || [],
                'internal'
              );
              if (halfBakedBeforeInternal.length > 0) {
                const wsHalfBakedBeforeInternal = XLSX.utils.json_to_sheet(halfBakedBeforeInternal);
                XLSX.utils.book_append_sheet(
                  workbook,
                  wsHalfBakedBeforeInternal,
                  'Before Internal Respondent'
                );
              }
            }

            // External Answers Comparison (by email)
            const externalComparison = createComparisonData(
              beforeData.externalAnswers || [],
              afterData.externalAnswers || [],
              'external',
              modulesData
            );

            if (externalComparison.length > 0) {
              const wsExternalComp = XLSX.utils.json_to_sheet(externalComparison);
              XLSX.utils.book_append_sheet(workbook, wsExternalComp, 'External B&A Analysis');
            }

            // Participant Answers Comparison (by participant name/email)
            const participantComparison = createComparisonData(
              beforeData.participantAnswers || [],
              afterData.participantAnswers || [],
              'participant',
              modulesData
            );

            if (participantComparison.length > 0) {
              const wsParticipantComp = XLSX.utils.json_to_sheet(participantComparison);
              XLSX.utils.book_append_sheet(workbook, wsParticipantComp, 'Participant B&A Analysis');
            }

            // Respondent Answers Comparison (by respondent name/email)
            const respondentComparison = createComparisonData(
              beforeData.respondentAnswers || [],
              afterData.respondentAnswers || [],
              'respondent',
              modulesData
            );

            if (respondentComparison.length > 0) {
              const wsRespondentComp = XLSX.utils.json_to_sheet(respondentComparison);
              XLSX.utils.book_append_sheet(workbook, wsRespondentComp, 'Respondent B&A Analysis');
            }
          }

          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });

          const filename = `${assessment?.name?.replace(/\s+/g, '_')}_Assessment_${hasBeforeData ? 'Before_After_Analysis_' : 'RAW_ONLY_'}${new Date().toISOString().split('T')[0]}.xlsx`;

          saveAs(blob, filename);
        } else {
          // Handle the fallback case (existing code for the else branch)
          const workbook = XLSX.utils.book_new();

          // Process fallback data; map External to requested columns
          const processedFallbackData = {
            externalAnswers: jsonData.externalAnswers || [],
            participantAnswers: jsonData.participantAnswers
              ? reorderColumns(excludeColumns(jsonData.participantAnswers), 'participant')
              : [],
            respondentAnswers: jsonData.respondentAnswers
              ? reorderColumns(excludeColumns(jsonData.respondentAnswers), 'respondent')
              : []
          };

          // External Respondent sheet (only if data exists)
          const externalFallbackRows = mapExternalAnswers(
            processedFallbackData.externalAnswers,
            assessment?.modules || []
          );
          if (externalFallbackRows.length > 0) {
            const wsExternal = XLSX.utils.json_to_sheet(externalFallbackRows);
            XLSX.utils.book_append_sheet(workbook, wsExternal, 'External Respondent');

            // Half Baked External Respondent sheet (fallback, only if data exists)
            const halfBakedExternalFallback = createHalfBakedSheet(
              processedFallbackData.externalAnswers,
              'external'
            );
            if (halfBakedExternalFallback.length > 0) {
              const wsHalfBakedExternal = XLSX.utils.json_to_sheet(halfBakedExternalFallback);
              XLSX.utils.book_append_sheet(
                workbook,
                wsHalfBakedExternal,
                'Half Baked External Respondent'
              );
            }
          }

          // Participant Answers sheet (only if data exists)
          if (processedFallbackData.participantAnswers.length > 0) {
            const wsParticipants = XLSX.utils.json_to_sheet(
              processedFallbackData.participantAnswers
            );
            XLSX.utils.book_append_sheet(workbook, wsParticipants, 'Participant Answers');

            // Half Baked Participant sheet (fallback, only if data exists)
            const halfBakedParticipantFallback = createHalfBakedSheet(
              jsonData.participantAnswers || [],
              'participant'
            );
            if (halfBakedParticipantFallback.length > 0) {
              const wsHalfBakedParticipant = XLSX.utils.json_to_sheet(halfBakedParticipantFallback);
              XLSX.utils.book_append_sheet(
                workbook,
                wsHalfBakedParticipant,
                'Half Baked Participant'
              );
            }
          }

          // Respondent Answers sheet (only if data exists)
          if (processedFallbackData.respondentAnswers.length > 0) {
            const wsRespondents = XLSX.utils.json_to_sheet(processedFallbackData.respondentAnswers);
            XLSX.utils.book_append_sheet(workbook, wsRespondents, 'Respondent Answers');

            // Half Baked Internal Respondent sheet (fallback, only if data exists)
            const halfBakedInternalFallback = createHalfBakedSheet(
              jsonData.respondentAnswers || [],
              'internal'
            );
            if (halfBakedInternalFallback.length > 0) {
              const wsHalfBakedInternal = XLSX.utils.json_to_sheet(halfBakedInternalFallback);
              XLSX.utils.book_append_sheet(
                workbook,
                wsHalfBakedInternal,
                'Half Baked Internal Respondent'
              );
            }
          }

          // Add General Analysis for fallback data (only if data exists)
          const generalAnalysisData = createGeneralAnalysisData(jsonData, null);
          if (generalAnalysisData.length > 0) {
            const wsGeneralAnalysis = XLSX.utils.json_to_sheet(generalAnalysisData);
            XLSX.utils.book_append_sheet(workbook, wsGeneralAnalysis, 'General Analysis');
          }

          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });

          const filename = `${assessment?.name?.replace(/\s+/g, '_')}_Assessment_Answers_${new Date().toISOString().split('T')[0]}.xlsx`;

          saveAs(blob, filename);
        }
      } catch (error) {
        alert('An error occurred while processing the assessment data. Please try again. ' + error);
      }
    } catch (error) {
      // Handle error
      alert(error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Container fluid>
        <Stack gap='lg'>
          {/* Breadcrumbs skeleton */}
          <Skeleton height={16} width={260} />

          <Grid gutter='lg'>
            {/* Left column */}
            <Grid.Col span={{ base: 12, lg: 8 }}>
              <Stack gap='md'>
                {/* Header card */}
                <Paper shadow='sm' p='lg' radius='md' withBorder>
                  <Stack gap={14}>
                    <Skeleton height={22} width={160} radius='xl' />
                    <Skeleton height={32} width='65%' />
                    <Skeleton height={20} width={80} radius='xl' />
                  </Stack>
                </Paper>

                {/* About card */}
                <Paper shadow='sm' p='lg' radius='md' withBorder>
                  <Skeleton height={20} width={180} mb='md' />
                  <Stack gap='xs'>
                    <Skeleton height={14} />
                    <Skeleton height={14} />
                    <Skeleton height={14} width='80%' />
                  </Stack>
                  <Skeleton height={20} width={160} mt='xl' mb='md' />
                  <Skeleton height={14} width='55%' />
                </Paper>

                {/* Assessment Details card */}
                <Paper shadow='sm' p='lg' radius='md' withBorder>
                  <Skeleton height={20} width={200} mb={24} />
                  {/* Module rows */}
                  <Stack gap='sm'>
                    {[1, 2, 3].map((i) => (
                      <Paper key={i} p='sm' withBorder>
                        <Skeleton height={14} width='100%' mb={8} />
                        <Skeleton height={12} width={150} />
                      </Paper>
                    ))}
                  </Stack>
                  {/* Participants & Respondents */}
                  <Stack mt={32} gap='md'>
                    <Group gap='xs'>
                      <Skeleton height={12} width={12} circle />
                      <Skeleton height={16} width={200} />
                    </Group>
                    <Group gap='xs'>
                      <Skeleton height={32} width={160} radius='sm' />
                      <Skeleton height={32} width={175} radius='sm' />
                      <Skeleton height={32} width={210} radius='sm' />
                    </Group>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Paper key={i} p='sm' withBorder>
                        <Group justify='space-between'>
                          <Group gap='xs'>
                            <Skeleton height={12} width={12} circle />
                            <Skeleton height={14} width={120} />
                          </Group>
                          <Skeleton height={22} width={70} radius='xl' />
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Grid.Col>

            {/* Right column */}
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <Paper shadow='sm' p='lg' radius='md' withBorder>
                <Skeleton height={20} width={160} mb='md' />
                <Stack gap='md'>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Box key={i}>
                      <Skeleton height={12} width={100} mb={6} />
                      <Skeleton height={16} width='80%' />
                    </Box>
                  ))}
                  <Skeleton height={36} mt='xs' />
                  <Skeleton height={36} />
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    );
  }

  // Error state
  if (error || !assessment) {
    return (
      <Container fluid>
        <Text>Error loading assessment details. Please try again.</Text>
      </Container>
    );
  }

  // Calculate status
  const now = new Date();
  const startTime = new Date(new Date(assessment.startTime).toLocaleString());
  const endTime = new Date(new Date(assessment.endTime).toLocaleString());

  let status = 'pending';
  if (assessment.isDeleted) {
    status = 'cancelled';
  } else if (now > endTime) {
    status = 'completed';
  } else if (now >= startTime && now <= endTime) {
    status = 'in progress';
  }

  return (
    <Container fluid>
      <Stack gap='lg'>
        <BreadcrumbsComponent
          items={[
            { title: 'Assessment Schedules', href: '/app/lia/assessment-schedules' },
            { title: 'Detail Schedules', href: '' }
          ]}
        />

        <Stack gap='md'>
          <Grid gutter='lg'>
            <Grid.Col span={{ base: 12, lg: 8 }}>
              <Stack gap={'md'}>
                <Card
                  withBorder
                  radius='lg'
                  padding='lg'
                  style={{ backgroundColor: '#0F172A', color: '#fff' }}
                >
                  <Group justify='space-between' align='flex-start'>
                    <Stack gap={14}>
                      <Group gap='xs'>
                        {status === 'completed' ? (
                          <Badge
                            leftSection={<IconCheck size={14} />}
                            color='teal'
                            variant='light'
                            size='lg'
                            radius='sm'
                          >
                            Completed on {dayjs(endTime).format('DD/MM/YYYY')}
                          </Badge>
                        ) : status === 'in progress' ? (
                          <Badge color='yellow' variant='light' size='lg' radius='sm'>
                            In Progress
                          </Badge>
                        ) : status === 'cancelled' ? (
                          <Badge color='red' variant='light' size='lg' radius='sm'>
                            Cancelled
                          </Badge>
                        ) : (
                          <Badge color='blue' variant='light' size='lg' radius='sm'>
                            Pending
                          </Badge>
                        )}
                      </Group>

                      <Title order={2} c='#fff'>
                        {assessment.name}
                      </Title>

                      <Badge color='gray' bg={'gray'} variant='solid' size='sm'>
                        {assessment.type}
                      </Badge>
                    </Stack>
                  </Group>
                </Card>

                <Card withBorder radius='lg' padding='lg'>
                  <Title order={4} mb='md'>
                    About this assessment
                  </Title>
                  <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {assessment.description}
                  </Text>

                  <Title order={4} mt='xl' mb='md'>
                    Assessment Notes
                  </Title>
                  <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {assessment.notes === '' ? '-' : assessment.notes}
                  </Text>
                </Card>

                <Card withBorder radius='lg' padding='lg'>
                  <Title order={4} mb={24}>
                    Assessment Details
                  </Title>
                  <ModuleSection moduleData={assessment.modules || []} />

                  <Stack mt={32}>
                    <Group>
                      <IconUsers size={14} />
                      <Text fw={600} fz={16}>
                        Participants & Respondents
                      </Text>
                    </Group>
                    <Tabs defaultValue='participants'>
                      <Tabs.List style={{ flexWrap: 'wrap', gap: 'var(--mantine-spacing-xs)' }}>
                        <Tabs.Tab value='participants'>Assessment Participants</Tabs.Tab>
                        <Tabs.Tab value='respondents'>Assessment Respondents</Tabs.Tab>
                        <Tabs.Tab value='externalRespondents'>
                          Assessment External Respondent
                        </Tabs.Tab>
                      </Tabs.List>

                      <Tabs.Panel value='participants' pt='md'>
                        <Group justify='flex-end' mb='md'>
                          <Button
                            leftSection={<IconDownload size={16} />}
                            variant='outline'
                            size='sm'
                            onClick={handleExportUnansweredParticipants}
                          >
                            Export Unanswered Participants
                          </Button>
                        </Group>
                        <ScrollArea offsetScrollbars>
                          <TableAssessmentParticipants
                            data={assessment?.participants || []}
                            hasSelfAssessment={
                              assessment?.modules?.some((module: any) =>
                                module?.questions?.some((q: any) => q?.respondentType === 'Self')
                              ) || false
                            }
                            handleViewResponse={(item) => {
                              setSelectedParticipant(item);
                              setEvidenceModalOpened(true);
                            }}
                          />
                        </ScrollArea>
                      </Tabs.Panel>

                      <Tabs.Panel value='respondents' pt='md'>
                        <Group justify='flex-end' mb='md'>
                          <Button
                            leftSection={<IconDownload size={16} />}
                            variant='outline'
                            size='sm'
                            onClick={handleExportUnansweredRespondents}
                          >
                            Export Unanswered Respondents
                          </Button>
                        </Group>
                        <ScrollArea offsetScrollbars>
                          <TableAssessmentRespondents
                            data={assessment?.respondents || []}
                            typeAssessment={assessment?.typeAssessment}
                            handleViewResponse={(item) => {
                              setSelectedRespondent(item);
                              setResponseModalOpened(true);
                            }}
                          />
                        </ScrollArea>
                      </Tabs.Panel>

                      <Tabs.Panel value='externalRespondents' pt='md'>
                        <ScrollArea offsetScrollbars>
                          <TableAssessmentRespondents
                            data={assessment?.respondentsExternal || []}
                            typeAssessment={assessment?.typeAssessment}
                            handleViewResponse={(item) => {
                              setSelectedRespondent(item);
                              setResponseModalOpened(true);
                            }}
                          />
                        </ScrollArea>
                      </Tabs.Panel>
                    </Tabs>
                  </Stack>
                </Card>
                {assessment.type === 'product' && (
                  <SubjectMatterExperts smes={assessment?.smes || []} />
                )}
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 4 }} pos={'relative'}>
              <DetailInformationSection
                key={id}
                assessment={assessment}
                activeModuleIndex={activeModuleIndex}
                handleDownload={toggleDownloadModal}
                // handleNotes={toggleNotesModal}
                handleEdit={() => navigate(`/app/lia/assessment-schedules/edit/${id}`)}
                setActiveModuleIndex={setActiveModuleIndex}
              />
            </Grid.Col>
          </Grid>
        </Stack>
      </Stack>
      <LIAReportModal
        opened={liaReportModalOpened}
        onClose={() => toggleLiaReportModal()}
        assessment={assessment}
        selectedCategory={selectedCategoryForLIA}
        onSaveContent={(content: any) => setLiaReportContent(content)}
      />

      <ModalNoteAssessment
        opened={notesModalOpened}
        onClose={toggleNotesModal}
        theText={assessment?.notes}
      />
      <DownloadModal
        opened={downloadModalOpened}
        onClose={() => setDownloadModalOpened(false)}
        assessmentId={id || ''}
        assessmentName={assessment?.name || ''}
        assessment={assessment}
        openLiaReportModal={toggleLiaReportModal}
        handleDownloadRawData={handleDownloadRawData}
      />

      <ModalRespondentResponse
        opened={responseModalOpened}
        onClose={() => setResponseModalOpened(false)}
        respondent={selectedRespondent}
        assessment={assessment}
      />

      {/* Participant Evidence Modal */}
      <Modal
        opened={evidenceModalOpened}
        onClose={() => setEvidenceModalOpened(false)}
        title={
          <Group>
            <Avatar color='#7B2183' radius='xl' size='md'>
              {selectedParticipant?.name ? selectedParticipant.name.charAt(0) : 'P'}
            </Avatar>
            <div>
              <Title order={4}>
                {selectedParticipant?.name || selectedParticipant?.binusianID || 'Participant'}'s
                Evidence
              </Title>
              <Text size='sm' c='dimmed'>
                Participant • {selectedParticipant?.email || 'No email available'}
              </Text>
            </div>
          </Group>
        }
        size='xl'
        p='lg'
        centered
        styles={{
          header: {
            backgroundColor: '#f8f9fa',
            padding: '16px 24px',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px'
          },
          body: { padding: '24px' }
        }}
      >
        {selectedParticipant ? (
          <Stack>
            <Tabs defaultValue='evidence'>
              <Tabs.List style={{ flexWrap: 'wrap', gap: 'var(--mantine-spacing-xs)' }}>
                <Tabs.Tab value='evidence'>Evidence Files</Tabs.Tab>
                <Tabs.Tab value='selfAssessment'>Self Assessment</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value='evidence' pt='md'>
                {selectedParticipant &&
                selectedParticipant.modules &&
                selectedParticipant.modules.length > 0 ? (
                  selectedParticipant.modules.map((module: any, moduleIndex: number) => {
                    // Check if module has evidence
                    const hasEvidence = module.evidences && module.evidences.length > 0;

                    return (
                      <Card
                        key={moduleIndex}
                        mb={16}
                        p={0}
                        radius='md'
                        withBorder
                        style={{ overflow: 'hidden' }}
                      >
                        <Box
                          p='md'
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderBottom: hasEvidence ? '1px solid #eee' : 'none'
                          }}
                        >
                          <Group gap={10} align='center'>
                            <IconFolder size={20} color='#7B2183' />
                            <Text fw={600} size='md'>
                              {module.moduleName}
                            </Text>
                          </Group>
                        </Box>

                        {hasEvidence ? (
                          <Box p='md'>
                            <SimpleGrid cols={1} spacing='md'>
                              {module.evidences.map((evidence: any, eIndex: number) => (
                                <Card key={eIndex} p='sm' withBorder radius='md' shadow='sm'>
                                  <Group justify='space-between'>
                                    <Group>
                                      <IconFileText size={18} />
                                      <Text size='sm' truncate>
                                        {evidence.fileName || 'File'}
                                      </Text>
                                    </Group>
                                    {evidence.evidenceUrl && (
                                      <Button
                                        size='xs'
                                        variant='light'
                                        color='#7B2183'
                                        component='a'
                                        href={evidence.evidenceUrl}
                                        target='_blank'
                                        rightSection={<IconExternalLink size={14} />}
                                      >
                                        View
                                      </Button>
                                    )}
                                  </Group>
                                  <Group justify='space-between' mt={4}>
                                    {evidence.fileSize && (
                                      <Badge size='sm' variant='outline' color='gray'>
                                        {evidence.fileSize}
                                      </Badge>
                                    )}
                                    {evidence.uploadDate && (
                                      <Text size='xs' c='dimmed'>
                                        {formatLocalDate(evidence.uploadDate, 'MMM D, YYYY')}
                                      </Text>
                                    )}
                                  </Group>
                                </Card>
                              ))}
                            </SimpleGrid>
                          </Box>
                        ) : (
                          <Box p='md' ta='center'>
                            <Text c='dimmed' fs='italic'>
                              No evidence submitted for this module.
                            </Text>
                          </Box>
                        )}
                      </Card>
                    );
                  })
                ) : (
                  <Box p='xl' ta='center'>
                    <IconClipboardX size={48} color='#7B2183' style={{ opacity: 0.5 }} />
                    <Text c='dimmed' fs='italic' mt={20}>
                      No modules or evidence found for this participant.
                    </Text>
                  </Box>
                )}
              </Tabs.Panel>

              <Tabs.Panel value='selfAssessment' pt='md'>
                {selectedParticipant && selectedParticipant.modules ? (
                  <Stack>
                    {selectedParticipant.modules.map((module: any, index: number) => {
                      const selfQuestions =
                        module.questions?.filter(
                          (q: { respondentType: string }) => q.respondentType === 'Self'
                        ) || [];

                      return (
                        <Card
                          key={index}
                          mb={16}
                          p={0}
                          radius='md'
                          withBorder
                          style={{ overflow: 'hidden' }}
                        >
                          <Box
                            p='md'
                            style={{
                              backgroundColor: '#f8f9fa',
                              borderBottom: selfQuestions.length > 0 ? '1px solid #eee' : 'none'
                            }}
                          >
                            <Group gap={10} align='center'>
                              <IconFolder size={20} color='#7B2183' />
                              <Text fw={600} size='md'>
                                {module.moduleName}
                              </Text>
                            </Group>
                          </Box>

                          {selfQuestions.length > 0 ? (
                            <Box p='md'>
                              <Stack gap={12}>
                                {selfQuestions.map((question: any, qIndex: number) => (
                                  <Card
                                    key={qIndex}
                                    withBorder
                                    p='md'
                                    radius='md'
                                    style={{ borderLeft: '3px solid #4dabf7' }}
                                  >
                                    <Stack gap={10} style={{ flex: 1 }}>
                                      {/* Question on its own line */}
                                      <Text fw={600} size='sm'>
                                        {question.title}
                                      </Text>
                                      {question.description && (
                                        <Text size='sm' c='dimmed'>
                                          {question.description}
                                        </Text>
                                      )}

                                      {/* Answer on a new line; preserve whitespace/newlines for free-text */}
                                      {question.type === 'Range' ? (
                                        <Group gap={8}>
                                          {question.answer && (
                                            <Badge
                                              size='lg'
                                              style={{ minWidth: 36, textAlign: 'center' }}
                                              color={
                                                parseInt(question.answer) > 3
                                                  ? 'green'
                                                  : parseInt(question.answer) < 2
                                                    ? 'red'
                                                    : 'yellow'
                                              }
                                            >
                                              {question.answer}
                                            </Badge>
                                          )}
                                          {question.answerLabel && (
                                            <Text size='sm' c='dimmed'>
                                              {question.answerLabel}
                                            </Text>
                                          )}
                                        </Group>
                                      ) : (
                                        <Box
                                          p='sm'
                                          style={{
                                            backgroundColor: 'var(--mantine-color-gray-1)',
                                            borderRadius: 8,
                                            fontSize: 14,
                                            lineHeight: 1.6,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                          }}
                                        >
                                          <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
                                            {question.answer || 'No answer provided'}
                                          </Text>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Card>
                                ))}
                              </Stack>
                            </Box>
                          ) : (
                            <Box p='md' ta='center'>
                              <Text c='dimmed' fs='italic'>
                                No self-assessment questions found for this module.
                              </Text>
                            </Box>
                          )}
                        </Card>
                      );
                    })}
                  </Stack>
                ) : participantSelfAssessment.length > 0 ? (
                  participantSelfAssessment.map((response: any, responseIndex: number) => (
                    <Box key={responseIndex}>
                      {response.modules && response.modules.length > 0 ? (
                        renderModulesAndQuestions(response.modules)
                      ) : (
                        <Text c='dimmed' ta='center'>
                          No self-assessment responses found.
                        </Text>
                      )}
                    </Box>
                  ))
                ) : (
                  <Box p='xl' ta='center'>
                    <IconClipboardX size={48} color='#7B2183' style={{ opacity: 0.5 }} />
                    <Text c='dimmed' fs='italic' mt={20}>
                      This participant has not submitted any self-assessment responses.
                    </Text>
                  </Box>
                )}
              </Tabs.Panel>
            </Tabs>

            <Group justify='flex-end' mt='xl'>
              <Button
                onClick={() => setEvidenceModalOpened(false)}
                color='#7B2183'
                radius='md'
                leftSection={<IconX size={16} />}
              >
                Close
              </Button>
            </Group>
          </Stack>
        ) : (
          <Center p='xl'>
            <Loader color='#7B2183' size='lg' />
          </Center>
        )}
      </Modal>
    </Container>
  );
};

const renderModulesAndQuestions = (modules: any[] | undefined) => {
  if (!modules || modules.length === 0) {
    return (
      <Box p='md' ta='center'>
        <Text c='dimmed' fs='italic'>
          No module data available
        </Text>
      </Box>
    );
  }

  return modules.map((module: any, mIndex: number) => (
    <Card key={mIndex} mb={16} p={0} radius='md' withBorder style={{ overflow: 'hidden' }}>
      {/* Module header */}
      <Box
        p='md'
        style={{
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #eee'
        }}
      >
        <Group gap={10} align='center'>
          <IconFolder size={20} color='#7B2183' />
          <Text fw={600} size='md'>
            {module.moduleID}
          </Text>
        </Group>
      </Box>

      {/* Group questions by respondentType in a simplified way */}
      {(() => {
        // Get unique respondent types with proper typing
        const respondentTypes: string[] = Array.from(
          new Set(module.questions?.map((q: any) => q.respondentType as string) || [])
        );

        return (
          <Box p='md'>
            <Tabs defaultValue={respondentTypes[0] || 'all'}>
              <Tabs.List mb='md' style={{ flexWrap: 'wrap', gap: 'var(--mantine-spacing-xs)' }}>
                {respondentTypes.map((respType: string) => (
                  <Tabs.Tab
                    key={respType}
                    value={respType}
                    leftSection={
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor:
                            respType === 'Self'
                              ? '#4dabf7'
                              : respType === 'Peer'
                                ? '#40c057'
                                : respType === 'Subordinate'
                                  ? '#fd7e14'
                                  : respType === 'Supervisor'
                                    ? '#7950f2'
                                    : 'gray'
                        }}
                      />
                    }
                  >
                    {respType}
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              {respondentTypes.map((respType: string) => (
                <Tabs.Panel key={respType} value={respType}>
                  <Stack gap={12}>
                    {module.questions
                      ?.filter((q: any) => q.respondentType === respType)
                      .map((question: any, qIndex: number) => (
                        <Card
                          key={qIndex}
                          withBorder
                          p='md'
                          radius='md'
                          style={{
                            borderLeft: `3px solid ${
                              respType === 'Self'
                                ? '#4dabf7'
                                : respType === 'Peer'
                                  ? '#40c057'
                                  : respType === 'Subordinate'
                                    ? '#fd7e14'
                                    : respType === 'Supervisor'
                                      ? '#7950f2'
                                      : 'gray'
                            }`
                          }}
                        >
                          <Group justify='space-between' align='center'>
                            <Stack gap={8} style={{ flex: 1 }}>
                              <Text fw={500} size='sm'>
                                {question.title}
                              </Text>
                            </Stack>

                            <Box>
                              {question.type === 'Range' ? (
                                <Badge
                                  size='lg'
                                  style={{
                                    minWidth: 36,
                                    textAlign: 'center'
                                  }}
                                  color={
                                    parseInt(question.answer) > 3
                                      ? 'green'
                                      : parseInt(question.answer) < 2
                                        ? 'red'
                                        : 'yellow'
                                  }
                                >
                                  {question.answer}
                                </Badge>
                              ) : (
                                <Box
                                  p='xs'
                                  style={{
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    maxWidth: '200px'
                                  }}
                                >
                                  {question.answer}
                                </Box>
                              )}
                            </Box>
                          </Group>
                        </Card>
                      ))}
                  </Stack>
                </Tabs.Panel>
              ))}
            </Tabs>

            {/* Display evidence if available */}
            {module.evidences && module.evidences.length > 0 && (
              <Box mt={20}>
                <Divider
                  my='md'
                  label={<Text fw={600}>Evidence Files</Text>}
                  labelPosition='center'
                />
                <SimpleGrid cols={2} spacing='md'>
                  {module.evidences.map((evidence: any, eIndex: number) => (
                    <Card key={eIndex} p='sm' withBorder radius='md' shadow='sm'>
                      <Group justify='space-between'>
                        <Group>
                          <IconFileText size={18} />
                          <Text size='sm' truncate>
                            {evidence.fileName || 'File'}
                          </Text>
                        </Group>
                        {evidence.evidenceUrl && (
                          <Button
                            size='xs'
                            variant='light'
                            color='#7B2183'
                            component='a'
                            href={evidence.evidenceUrl}
                            target='_blank'
                            rightSection={<IconExternalLink size={14} />}
                          >
                            View
                          </Button>
                        )}
                      </Group>
                      <Group justify='space-between' mt={4}>
                        {evidence.fileSize && (
                          <Badge size='sm' variant='outline' color='gray'>
                            {evidence.fileSize}
                          </Badge>
                        )}
                        {evidence.uploadDate && (
                          <Text size='xs' c='dimmed'>
                            {formatLocalDate(evidence.uploadDate, 'MMM D, YYYY')}
                          </Text>
                        )}
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </Box>
        );
      })()}
    </Card>
  ));
};

export default AssessmentDetailPage;
